import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { ArrowRight, Compass, Target, MessageCircle, Calendar, Sparkles } from "lucide-react";
import { PublicShell } from "@/components/layouts/PublicShell";
import { Seo } from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { type } from "@/lib/typography";
import type { Lang } from "@/content/tracks";

import PillarFAQ from "@/components/shared/PillarFAQ";
import MentorApplicationModal from "@/components/shared/MentorApplicationModal";
import MentorPackageCard from "@/components/mentoring/MentorPackageCard";
import DiscoveryCallModal from "@/components/shared/DiscoveryCallModal";
import BecomeProviderCTA from "@/components/shared/BecomeProviderCTA";

const heroGradient =
  "radial-gradient(1200px 600px at 15% -10%, hsl(var(--primary) / 0.18) 0%, transparent 55%), radial-gradient(900px 500px at 95% 30%, hsl(var(--accent) / 0.12) 0%, transparent 60%), linear-gradient(180deg, hsl(var(--background)) 0%, hsl(var(--muted) / 0.4) 100%)";

type PackageSlug = "strategic-mentoring" | "turnaround-sprint" | "board-readiness";

export default function MentoringPillar() {
  const { i18n } = useTranslation();
  const lang: Lang = i18n.language?.startsWith("fr") ? "fr" : "en";
  const isFr = lang === "fr";

  const [applicationOpen, setApplicationOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<{
    slug: PackageSlug;
    name: { en: string; fr: string };
  } | null>(null);
  const [discoveryOpen, setDiscoveryOpen] = useState(false);

  function handleApply(slug: PackageSlug, name: { en: string; fr: string }) {
    setSelectedPackage({ slug, name });
    setApplicationOpen(true);
  }

  // ---- Bilingual existing copy (translated as part of Sprint C, Instruction 1) ----
  const VALUES = [
    {
      icon: Compass,
      title: isFr ? "Des opérateurs, pas des coachs à louer" : "Operators, not coaches-for-hire",
      body: isFr
        ? "Chaque mentor a expédié une entreprise dont vous avez entendu parler — ou en a discrètement bâti une qui mérite d'être connue."
        : "Every mentor has shipped a company you've heard of — or built one quietly worth knowing about.",
    },
    {
      icon: Target,
      title: isFr ? "Adapté à votre stade" : "Matched to your stage",
      body: isFr
        ? "Pré-seed, post-PMF, en scaling — nous vous mettons en relation avec quelqu'un qui a résolu votre problème exact il y a 18 mois."
        : "Pre-seed, post-PMF, scaling — we pair you with someone who solved your exact problem 18 months ago.",
    },
    {
      icon: MessageCircle,
      title: isFr ? "Long-terme, pas transactionnel" : "Long-horizon, not transactional",
      body: isFr
        ? "Le mentoring est une relation de 6 à 12 mois. Du vrai contexte. De la vraie redevabilité. De la vraie conviction quand ça compte."
        : "Mentoring is a 6–12 month relationship. Real context. Real accountability. Real conviction when it matters.",
    },
  ];

  const STEPS = [
    {
      n: "01",
      title: isFr ? "Candidater" : "Apply",
      body: isFr
        ? "Dites-nous votre stade, votre marché, et le mur sur lequel vous butez sans cesse."
        : "Tell us your stage, market, and the wall you keep hitting.",
    },
    {
      n: "02",
      title: isFr ? "Être mis en relation" : "Get matched",
      body: isFr
        ? "Bruno examine votre candidature personnellement. Si le fit est là, vous aurez un appel découverte. Sinon, il vous dit pourquoi et vous oriente ailleurs."
        : "Bruno reviews your application personally. If it's a fit, you get a discovery call. If it's not, he tells you why and points you elsewhere.",
    },
    {
      n: "03",
      title: isFr ? "Se voir chaque mois" : "Meet monthly",
      body: isFr
        ? "Séances de 60 min. Async entre les deux. Revue trimestrielle des résultats."
        : "60-min sessions. Async between. Quarterly review on outcomes.",
    },
  ];

  // ---- FAQ items (verbatim from spec) ----
  const FAQ_ITEMS = [
    {
      question: { en: "Why only Bruno as a mentor?", fr: "Pourquoi uniquement Bruno comme mentor ?" },
      answer: {
        en: "Mentoring is a 6-12 month relationship of real consequence. You're trusting someone to know your cap table, your decisions, and what you said three months ago. Bootpreneurs would rather offer one mentor with a real operator track record than a catalog of names we can't stand behind. When we add others, they'll clear the same bar Bruno set.",
        fr: "Le mentoring est une relation de 6 à 12 mois avec de vraies conséquences. Vous faites confiance à quelqu'un pour connaître votre cap table, vos décisions, et ce que vous avez dit il y a trois mois. Bootpreneurs préfère offrir un mentor avec un vrai historique d'opérateur qu'un catalogue de noms qu'on ne peut pas défendre. Quand d'autres rejoindront, ils devront passer la même barre que Bruno a fixée.",
      },
    },
    {
      question: { en: "How is this different from your Coaching offer?", fr: "En quoi est-ce différent de votre offre Coaching ?" },
      answer: {
        en: "Coaching is weekly tactical sparring on specific problems, typically 3-6 months, priced per session or per hour (79k-131k XOF/hour depending on coach). Mentoring is a longer-horizon relationship with Bruno specifically — the person who built this platform — focused on strategic and governance decisions that don't fit into a one-hour conversation. Coaching moves metrics; mentoring shapes the trajectory of the company. Both can be right.",
        fr: "Le coaching est un sparring tactique hebdomadaire sur des problèmes précis, typiquement 3 à 6 mois, tarifé à la séance ou à l'heure (79k-131k XOF/heure selon le coach). Le mentoring est une relation à horizon plus long avec Bruno spécifiquement — la personne qui a construit cette plateforme — axée sur les décisions stratégiques et de gouvernance qui ne tiennent pas en une heure. Le coaching fait bouger les indicateurs ; le mentoring façonne la trajectoire de l'entreprise. Les deux peuvent être justes.",
      },
    },
    {
      question: { en: "How much of Bruno's time do I actually get?", fr: "Quelle part du temps de Bruno j'obtiens réellement ?" },
      answer: {
        en: "Strategic Mentoring: one 90-minute session per month plus async via email/WhatsApp between sessions (typical response time 24-48 hours, excluding weekends). Turnaround Sprint: weekly 2-hour working sessions plus async access for the 12-week engagement. Board Readiness: scope confirmed after application — typically quarterly sessions plus on-call availability for critical transaction moments.",
        fr: "Mentoring Stratégique : une séance de 90 minutes par mois plus accès asynchrone par email/WhatsApp entre les séances (temps de réponse typique 24-48h, hors weekend). Sprint Redressement : séances de travail hebdomadaires de 2 heures plus accès asynchrone pendant l'engagement de 12 semaines. Préparation à la Transaction : périmètre confirmé après candidature — typiquement séances trimestrielles plus disponibilité pour les moments critiques de la transaction.",
      },
    },
    {
      question: { en: "What does the application process look like?", fr: "À quoi ressemble le processus de candidature ?" },
      answer: {
        en: "Short form: your business, your stage, what you're trying to figure out or build. Bruno reviews personally within 5 business days. If it's a fit, you get an invitation to a 30-minute discovery call. If not, Bruno tells you why — and if there's a better fit elsewhere on the platform (Coaching, Fractional) or outside, he'll say so. Not every founder needs a mentor; he'll tell you.",
        fr: "Formulaire court : votre business, votre stade, ce que vous cherchez à comprendre ou à construire. Bruno examine personnellement sous 5 jours ouvrés. Si le fit est là, vous recevez une invitation à un appel découverte de 30 minutes. Sinon, Bruno vous dit pourquoi — et s'il y a une meilleure solution ailleurs sur la plateforme (Coaching, Fractional) ou à l'extérieur, il le dira. Tous les dirigeants n'ont pas besoin d'un mentor ; il vous le dira.",
      },
    },
    {
      question: { en: "Which languages does Bruno mentor in?", fr: "Dans quelles langues Bruno fait-il du mentoring ?" },
      answer: {
        en: "French and English, both at professional fluency. Bruno spent his career across France, UK, and Francophone Africa — the language follows the context of your business and your team.",
        fr: "Français et anglais, à un niveau professionnel dans les deux. Bruno a passé sa carrière entre la France, le Royaume-Uni et l'Afrique francophone — la langue suit le contexte de votre business et de votre équipe.",
      },
    },
    {
      question: { en: "What happens if the mentoring relationship isn't working?", fr: "Que se passe-t-il si la relation de mentoring ne fonctionne pas ?" },
      answer: {
        en: "Tell Bruno directly. If the fit is off and can't be repaired, Strategic Mentoring allows exit with a 30-day notice (remaining prepaid months refunded). Turnaround Sprint is a fixed commitment — if you exit early, you keep the deliverables completed to that point, no refund on the engagement fee. Board Readiness terms are agreed case-by-case at application. The goal is real partnership, not lock-in.",
        fr: "Dites-le directement à Bruno. Si le fit ne marche pas et ne peut être réparé, le Mentoring Stratégique permet de sortir avec un préavis de 30 jours (mois restants pré-payés remboursés). Le Sprint Redressement est un engagement ferme — si vous sortez en avance, vous gardez les livrables jusque-là, pas de remboursement des frais d'engagement. Les conditions de la Préparation à la Transaction sont convenues cas par cas à la candidature. L'objectif est un vrai partenariat, pas le lock-in.",
      },
    },
  ];

  return (
    <PublicShell>
      <Seo
        title={isFr ? "Mentoring — Bootpreneurs" : "Mentoring — Bootpreneurs"}
        description={
          isFr
            ? "Accompagnement long-terme de fondateurs seniors qui ont expédié. Adapté à votre stade, votre marché, votre mur."
            : "Long-term guidance from senior founders who've shipped. Matched to your stage, your market, your wall."
        }
      />

      {/* Hero */}
      <div style={{ background: heroGradient }} className="border-b border-border">
        <section className="container py-24 md:py-32">
          <p className="mb-5 text-accent" style={type.eyebrow}>
            ◆ {isFr ? "Pôle 03 — Mentoring" : "Pillar 03 — Mentoring"}
          </p>
          <h1 className="max-w-[18ch] text-foreground" style={type.displayXl}>
            {isFr
              ? "Des fondateurs seniors. Dans votre coin. Sur la durée."
              : "Senior founders. In your corner. For the long haul."}
          </h1>
          <div className="mt-8 flex flex-wrap items-center gap-7">
            <p className="max-w-xl text-muted-foreground" style={type.bodyLg}>
              {isFr
                ? "Arrêtez de louer la stratégie à l'heure. Trouvez un mentor qui connaît votre cap table, votre roadmap, et ce que vous avez dit il y a trois mois."
                : "Stop renting strategy by the hour. Get a mentor who knows your cap table, your roadmap, and what you said three months ago."}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => {
                  const el = document.getElementById("mentoring-packages");
                  el?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="inline-flex items-center gap-2 rounded-full bg-foreground px-7 py-3.5 text-sm font-semibold text-background transition hover:opacity-90"
              >
                {isFr ? "Voir les packages" : "Browse packages"} <ArrowRight className="h-4 w-4" />
              </button>
              <Link
                to="/services/coaching"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-7 py-3.5 text-sm font-semibold text-foreground transition hover:border-primary"
              >
                {isFr ? "Plutôt du coaching 1:1 ?" : "Need 1:1 coaching instead?"}
              </Link>
            </div>
          </div>
        </section>
      </div>

      {/* Why mentoring */}
      <section className="container py-20 md:py-28">
        <p className="mb-4 text-accent" style={type.eyebrow}>
          ─── {isFr ? "Pourquoi le mentoring" : "Why mentoring"}
        </p>
        <h2 className="max-w-[24ch] text-foreground" style={type.displayLg}>
          {isFr
            ? "La différence entre un conseil et une conviction."
            : "The difference between advice and conviction."}
        </h2>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {VALUES.map((v) => (
            <div
              key={v.title}
              className="rounded-[18px] border border-border bg-card p-7 transition hover:-translate-y-[3px] hover:border-primary"
            >
              <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                <v.icon className="h-5 w-5 text-primary" />
              </div>
              <h3 className="mb-2 text-foreground" style={type.panelTitle}>{v.title}</h3>
              <p className="text-muted-foreground" style={type.bodySm}>{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION A — Three ways to work with Bruno */}
      <section id="mentoring-packages" className="container py-20 md:py-28 border-t border-border">
        <p className="mb-4 text-accent" style={type.eyebrow}>
          ─── {isFr ? "PACKAGES" : "PACKAGES"}
        </p>
        <h2 className="max-w-[24ch] text-foreground" style={type.displayLg}>
          {isFr ? "Trois façons de travailler avec Bruno" : "Three ways to work with Bruno"}
        </h2>
        <p className="mt-5 max-w-3xl text-muted-foreground" style={type.bodyLg}>
          {isFr
            ? "Chaque package est sur candidature et délivré personnellement par Bruno. Bootpreneurs fixe le standard et garantit l'engagement."
            : "Each package is application-only and personally delivered by Bruno. Bootpreneurs holds the standard and guarantees the engagement."}
        </p>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          <MentorPackageCard
            slug="strategic-mentoring"
            title={{ en: "Strategic Mentoring", fr: "Mentoring Stratégique" }}
            subtitle={{
              en: "The operator in your corner for 12 months",
              fr: "L'opérateur à vos côtés pendant 12 mois",
            }}
            description={{
              en: "Monthly 90-minute sessions with Bruno, plus async access between sessions. Quarterly reviews on what's moved, what's next, and whether to keep going. For founders past PMF who need operator-grade judgment on scaling decisions that would otherwise sit in their head alone.",
              fr: "Séances mensuelles de 90 minutes avec Bruno, accès asynchrone entre les séances. Revue trimestrielle de ce qui a bougé, la suite, et si on continue. Pour les dirigeants post-PMF qui ont besoin d'un jugement d'opérateur sur les décisions de scaling qui traînent sinon dans leur tête seul.",
            }}
            whoItsFor={{
              en: [
                "Founders post-PMF with real P&L",
                "Scaling decisions that no one around you can challenge",
                "You want continuity — not advice by the hour",
              ],
              fr: [
                "Dirigeants post-PMF avec un vrai P&L",
                "Décisions de scaling que personne autour de vous ne peut challenger",
                "Vous voulez de la continuité — pas du conseil à l'heure",
              ],
            }}
            duration={{ en: "12 months", fr: "12 mois" }}
            price={{
              en: "1 500 000 XOF / ~€2,300 per month",
              fr: "1 500 000 XOF / ~2 300 € par mois",
            }}
            priceIndicator={null}
            lang={lang}
            onApply={() =>
              handleApply("strategic-mentoring", {
                en: "Strategic Mentoring",
                fr: "Mentoring Stratégique",
              })
            }
          />

          <MentorPackageCard
            slug="turnaround-sprint"
            title={{ en: "Turnaround Sprint", fr: "Sprint Redressement" }}
            subtitle={{
              en: "3 months of intensive operator focus on the hardest problem in your business",
              fr: "3 mois de focus intense d'opérateur sur le problème le plus difficile de votre business",
            }}
            description={{
              en: "Weekly 2-hour working sessions with Bruno for 12 weeks. For founders facing a defined inflection: runway collapse, team failure, market shift, product decision that could break the company. Bruno brings the same decision-making he used across multiple turnarounds and capacity expansions.",
              fr: "Séances de travail hebdomadaires de 2 heures avec Bruno pendant 12 semaines. Pour les dirigeants face à une inflexion définie : cash-runway qui se réduit, échec d'équipe, changement de marché, décision produit qui peut casser l'entreprise. Bruno apporte la même capacité de décision qu'il a appliquée sur de multiples redressements et expansions de capacité.",
            }}
            whoItsFor={{
              en: [
                "A real, urgent inflection point",
                "You can dedicate 2 hours/week for 12 weeks",
                "You need someone who has broken-and-rebuilt before",
              ],
              fr: [
                "Un point d'inflexion réel et urgent",
                "Vous pouvez dédier 2 heures/semaine pendant 12 semaines",
                "Vous avez besoin de quelqu'un qui a déjà cassé-et-reconstruit",
              ],
            }}
            duration={{ en: "3 months", fr: "3 mois" }}
            price={{
              en: "4 500 000 XOF / ~€6,900 flat",
              fr: "4 500 000 XOF / ~6 900 € forfait",
            }}
            priceIndicator={null}
            lang={lang}
            onApply={() =>
              handleApply("turnaround-sprint", {
                en: "Turnaround Sprint",
                fr: "Sprint Redressement",
              })
            }
          />

          <MentorPackageCard
            slug="board-readiness"
            title={{ en: "Board Readiness", fr: "Préparation à la Transaction" }}
            subtitle={{
              en: "Prepare for the transaction that changes the company",
              fr: "Préparer la transaction qui change l'entreprise",
            }}
            description={{
              en: "Long-horizon engagement for founders preparing for Series A/B fundraising, exit, acquisition, or shareholder restructuring. Quarterly strategic sessions, on-call for critical decisions, direct input on investor materials and governance. Drawing on Bruno's experience closing a share-sale divestment in October 2025 and architecting multi-year strategies approved at Group level.",
              fr: "Accompagnement long-horizon pour les dirigeants préparant une levée Série A/B, une sortie, une acquisition, ou une restructuration actionnariale. Sessions stratégiques trimestrielles, disponibilité pour décisions critiques, contribution directe aux documents investisseurs et à la gouvernance. S'appuie sur l'expérience de Bruno qui a clôturé une cession par vente d'actions en octobre 2025 et architecturé des stratégies pluriannuelles approuvées au niveau Groupe.",
            }}
            whoItsFor={{
              en: [
                "You're 6-18 months from a material transaction",
                "Stakes high enough that operator-grade preparation matters",
                "Application reviewed personally by Bruno",
              ],
              fr: [
                "Vous êtes à 6-18 mois d'une transaction matérielle",
                "Les enjeux sont suffisamment élevés pour justifier une préparation d'opérateur",
                "Candidature examinée personnellement par Bruno",
              ],
            }}
            duration={{ en: "6-9 months", fr: "6-9 mois" }}
            price={null}
            priceIndicator={{ en: "By application", fr: "Sur candidature" }}
            lang={lang}
            onApply={() =>
              handleApply("board-readiness", {
                en: "Board Readiness",
                fr: "Préparation à la Transaction",
              })
            }
          />
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-border bg-muted/30">
        <div className="container py-20 md:py-28">
          <p className="mb-4 text-accent" style={type.eyebrow}>
            ─── {isFr ? "Comment ça marche" : "How it works"}
          </p>
          <h2 className="max-w-[20ch] text-foreground" style={type.displayLg}>
            {isFr ? "Trois étapes. Engagement de six mois." : "Three steps. Six-month commitment."}
          </h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {STEPS.map((s) => (
              <div key={s.n} className="rounded-[18px] border border-border bg-card p-7">
                <div className="mb-4 font-mono text-xs text-muted-foreground">{s.n}</div>
                <h3 className="mb-2 text-foreground" style={type.panelTitle}>{s.title}</h3>
                <p className="text-muted-foreground" style={type.bodySm}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION C — FAQ */}
      <PillarFAQ
        lang={lang}
        items={FAQ_ITEMS}
        sectionHeadline={{
          en: "Common questions about mentoring",
          fr: "Questions fréquentes sur le mentoring",
        }}
      />

      {/* SECTION D — Final CTA (updated) */}
      <section className="container py-20 md:py-28 text-center">
        <Sparkles className="mx-auto mb-5 h-7 w-7 text-accent" />
        <h2 className="mx-auto max-w-[22ch] text-foreground" style={type.displayMd}>
          {isFr ? "Prêt à arrêter de deviner ?" : "Ready to stop guessing?"}
        </h2>
        <p className="mx-auto mt-5 max-w-2xl text-muted-foreground" style={type.bodyLg}>
          {isFr
            ? "Trois façons de travailler avec Bruno. Chaque candidature examinée personnellement sous 5 jours ouvrés."
            : "Three ways to work with Bruno. Each application reviewed personally within 5 business days."}
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <Button
            size="lg"
            onClick={() => {
              const el = document.getElementById("mentoring-packages");
              el?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="min-w-[200px]"
          >
            {isFr ? "Voir les packages" : "Browse packages"}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <button
            onClick={() => setDiscoveryOpen(true)}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground underline underline-offset-4 transition hover:text-foreground"
          >
            <Calendar className="h-4 w-4" />
            {isFr ? "Encore hésitant ? Réservez un échange" : "Still deciding? Book a conversation"}
          </button>
        </div>
      </section>

      {/* SECTION E — Modals */}
      {selectedPackage && (
        <MentorApplicationModal
          open={applicationOpen}
          onOpenChange={setApplicationOpen}
          packageSlug={selectedPackage.slug}
          packageName={selectedPackage.name}
          lang={lang}
        />
      )}

      <DiscoveryCallModal
        open={discoveryOpen}
        onOpenChange={setDiscoveryOpen}
        sourcePillar="mentoring"
        lang={lang === "fr" ? "fr" : "en"}
      />
      <div className="container mx-auto px-4">
        <BecomeProviderCTA kind="mentor" />
      </div>
    </PublicShell>
  );
}
